import React from 'react';
import moment, { Moment } from 'moment-timezone';
import useTimeZone from 'hooks/useTimeZone';
import { ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector';
import { getInitialDateWithValidTime, getTimeOptionsForDateRange } from 'components/PromoCodes/Utils';
import { DiscountType, SavePromocode } from '@spike/promocode-model';

export const PROMO_CODE_NAME_MAX_LENGTH = 35;

export interface FormState {
    name: { value: string; error: string };
    value: {
        value: number;
        type: DiscountType;
        error: string;
    };
    olb: boolean;
    startAt: Moment;
    endAt: Moment;
    endAtEnabled: boolean;
}

interface ExpandedSelectors {
    startAt: boolean;
    endAt: boolean;
}

export const usePromoCodeDetailsStep = (promoCode: SavePromocode, lockConfiguration = false) => {
    const timeZone = useTimeZone();

    const today = moment.tz(timeZone);
    const initialTimeOptions = getTimeOptionsForDateRange(today);
    const todayWithValidTime = getInitialDateWithValidTime(initialTimeOptions, timeZone);

    const initializeFormState = React.useCallback((): FormState => {
        const defaultstartAt = moment.tz(timeZone).hour(8).minute(0).second(0) as Moment;
        const defaultendAt = moment.tz(timeZone).hour(8).minute(0).second(0) as Moment;

        let startAt = defaultstartAt;
        let endAt = defaultendAt;

        if (promoCode.startAt) {
            // Ensure the start date time is valid for the available options
            const startTimeOptions = getTimeOptionsForDateRange(promoCode.startAt);
            const currentStartTime = promoCode.startAt.format('hh:mma');
            const validStartTime = startTimeOptions.find(option => option.id === currentStartTime);

            if (validStartTime) {
                startAt = promoCode.startAt.clone();
            } else {
                // If the time isn't valid, use the date but with default time
                startAt = promoCode.startAt.clone().tz(timeZone).hour(8).minute(0).second(0);
            }
        }

        if (promoCode.endAt?.isValid) {
            // Ensure the end date time is valid for the available options
            const endTimeOptions = getTimeOptionsForDateRange(promoCode.endAt);
            const currentEndTime = promoCode.endAt.format('hh:mma');
            const validEndTime = endTimeOptions.find(option => option.id === currentEndTime);

            if (validEndTime) {
                endAt = promoCode.endAt.clone();
            } else {
                // If the time isn't valid, use the date but with default time
                endAt = promoCode.endAt.clone().tz(timeZone).hour(8).minute(0).second(0);
            }
        }

        return {
            name: { value: promoCode.name || '', error: '' },
            value: {
                value: promoCode.discountAmount || 0,
                type: promoCode.discountType || DiscountType.FIXED,
                error: ''
            },
            olb: promoCode.olb || false,
            startAt,
            endAt,
            endAtEnabled: Boolean(promoCode.endAt?.isValid) || false
        };
    }, [promoCode]);

    const [formState, setFormState] = React.useState<FormState>(initializeFormState);

    const [expandedSelectors, setExpandedSelectors] = React.useState<ExpandedSelectors>({
        startAt: false,
        endAt: false
    });

    const isValid = React.useMemo(() => {
        const isNameValid =
            formState.name.error.length === 0 &&
            formState.name.value.length > 0 &&
            formState.value.error.length === 0 &&
            formState.value.value !== 0;

        return isNameValid;
    }, [formState.name, formState.value]);

    const handlePromoCodeNameChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (lockConfiguration) return;
            const value = e.target.value.slice(0, PROMO_CODE_NAME_MAX_LENGTH);
            setFormState(prev => ({
                ...prev,
                name: {
                    value,
                    error: value.length === 0 ? 'Promo Code name is required' : ''
                }
            }));
        },
        [lockConfiguration]
    );

    const handleValueChange = React.useCallback(
        (value: ValueTypeSelectorValue) => {
            if (lockConfiguration) return;
            setFormState(prev => ({
                ...prev,
                value: {
                    value: value.value,
                    type: value.type === 'amount' ? DiscountType.FIXED : DiscountType.PERCENTAGE,
                    error: value.value === 0 ? 'Promo Code value is required' : ''
                }
            }));
        },
        [lockConfiguration]
    );

    const startTimeOptions = React.useMemo(() => {
        return getTimeOptionsForDateRange(formState.startAt);
    }, [formState.startAt]);

    const endTimeOptions = React.useMemo(() => {
        return getTimeOptionsForDateRange(formState.endAt);
    }, [formState.endAt]);

    const createTimeChangeHandler = React.useCallback(
        (type: 'start' | 'end') => (value: string) => {
            if (lockConfiguration) return;
            const currentDate = type === 'start' ? formState.startAt : formState.endAt;
            const time = moment.tz(value, 'hh:mma', timeZone);

            const newDate = currentDate.clone().tz(timeZone).set({
                hour: time.hour(),
                minute: time.minute()
            });

            setFormState(prev => ({
                ...prev,
                [type === 'start' ? 'startAt' : 'endAt']: newDate
            }));
        },
        [formState.startAt, formState.endAt, timeZone, lockConfiguration]
    );

    const createDateChangeHandler = React.useCallback(
        (type: 'start' | 'end') => (value: Moment | null) => {
            if (lockConfiguration || !value) return;

            const currentDate = type === 'start' ? formState.startAt : formState.endAt;
            const newDateTimeOptions = getTimeOptionsForDateRange(value);
            const currentTime = currentDate.format('hh:mma');
            const currentTimeValid = newDateTimeOptions.find(option => option.id === currentTime);

            const timeToUse = currentTimeValid ? currentTime : newDateTimeOptions[0].id;
            const timeToUseMoment = moment.tz(timeToUse, 'hh:mma', timeZone);

            const newDate = value.clone().tz(timeZone).set({
                hour: timeToUseMoment.hour(),
                minute: timeToUseMoment.minute()
            });

            setFormState(prev => ({
                ...prev,
                [type === 'start' ? 'startAt' : 'endAt']: newDate
            }));

            // Force close the expanded selector when date is selected
            setTimeout(() => {
                setExpandedSelectors(prev => ({
                    ...prev,
                    [type === 'start' ? 'startAt' : 'endAt']: false
                }));
            }, 100);
        },
        [formState.startAt, formState.endAt, timeZone, lockConfiguration]
    );

    const handleEndAtClickAway = React.useCallback(() => {
        setExpandedSelectors(prev => {
            if (prev.endAt) {
                return { ...prev, endAt: false };
            }
            return prev;
        });
    }, []);

    const handleExpandSelector = React.useCallback(
        (type: 'startAt' | 'endAt') => {
            if (lockConfiguration) return;
            setExpandedSelectors(prev => ({ ...prev, [type]: !prev[type] }));
        },
        [lockConfiguration]
    );

    const handleClickAwaySelector = React.useCallback((type: 'startAt' | 'endAt') => {
        setExpandedSelectors(prev => ({ ...prev, [type]: false }));
    }, []);

    const toggleEndAtEnabled = React.useCallback(() => {
        if (lockConfiguration) return;
        setFormState(prev => ({ ...prev, endAtEnabled: !prev.endAtEnabled }));
    }, [lockConfiguration]);

    const handleOlbChange = React.useCallback((checked: boolean) => {
        setFormState(prev => ({ ...prev, olb: checked }));
    }, []);

    const getFormData = React.useCallback((): Partial<SavePromocode> => {
        if (lockConfiguration) {
            return { olb: formState.olb };
        }

        return {
            name: formState.name.value,
            discountAmount: formState.value.value,
            discountType: formState.value.type,
            startAt: formState.startAt,
            endAt: formState.endAtEnabled ? formState.endAt : undefined,
            olb: formState.olb
        };
    }, [formState, lockConfiguration]);

    return {
        formState,
        expandedSelectors,
        isValid,
        startTimeOptions,
        endTimeOptions,
        today: todayWithValidTime,
        timeZone,
        handlers: {
            handlePromoCodeNameChange,
            handleValueChange,
            createTimeChangeHandler,
            createDateChangeHandler,
            handleEndAtClickAway,
            handleExpandSelector,
            handleClickAwaySelector,
            toggleEndAtEnabled,
            handleOlbChange
        },
        getFormData
    };
};
